import React from 'react';

/**
 * NextBestAction (NBA) Component
 *
 * The backbone of liquid design in enterprise workflow tools.
 * Shows the recommended next step with one primary action.
 *
 * Usage:
 * <NextBestAction
 *   action="Run AutoMatch"
 *   description="12 profiles ready for matching"
 *   onClick={() => handleAutoMatch()}
 *   why="Profiles uploaded but not matched yet"
 *   variant="primary" // primary | secondary | subtle
 *   icon="sparkles" // sparkles | arrow | search | calendar | upload
 * />
 */

const iconMap = {
  sparkles: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  ),
  arrow: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
    </svg>
  ),
  search: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  calendar: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  upload: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  ),
  check: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  users: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  settings: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
};

const variantStyles = {
  primary: {
    container: 'bg-accent-soft border-blue-500/20',
    icon: 'text-accent-ink bg-blue-500/15',
    title: 'text-ink',
    description: 'text-ink-muted',
    button: 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm',
    why: 'text-blue-400 hover:text-blue-300',
  },
  success: {
    container: 'bg-green-500/[0.08] border-green-500/20',
    icon: 'text-green-400 bg-green-500/15',
    title: 'text-green-300',
    description: 'text-ink-muted',
    button: 'bg-green-600 hover:bg-green-700 text-white shadow-sm',
    why: 'text-green-400 hover:text-green-300',
  },
  warning: {
    container: 'bg-amber-500/[0.08] border-amber-500/20',
    icon: 'text-amber-400 bg-amber-500/15',
    title: 'text-amber-300',
    description: 'text-ink-muted',
    button: 'bg-amber-600 hover:bg-amber-700 text-white shadow-sm',
    why: 'text-amber-400 hover:text-amber-300',
  },
  secondary: {
    container: 'bg-muted border-line',
    icon: 'text-ink-muted bg-subtle',
    title: 'text-ink',
    description: 'text-ink-muted',
    button: 'bg-gray-800 hover:bg-gray-900 text-white shadow-sm',
    why: 'text-ink-subtle hover:text-ink-muted',
  },
  subtle: {
    container: 'bg-elevated border-line-subtle',
    icon: 'text-ink-subtle bg-muted',
    title: 'text-ink',
    description: 'text-ink-subtle',
    button: 'bg-subtle hover:bg-subtle text-ink',
    why: 'text-ink-subtle hover:text-ink-muted',
  },
};

export const NextBestAction = ({
  action,
  description,
  onClick,
  why,
  variant = 'primary',
  icon = 'arrow',
  buttonText,
  secondaryAction,
  onSecondaryClick,
  loading = false,
  disabled = false,
  compact = false,
}) => {
  const styles = variantStyles[variant] || variantStyles.primary;
  const IconComponent = iconMap[icon] || iconMap.arrow;

  if (compact) {
    return (
      <div className={`flex items-center justify-between px-4 py-3 rounded-lg border ${styles.container}`}>
        <div className="flex items-center gap-3">
          <div className={`p-1.5 rounded-lg ${styles.icon}`}>
            {IconComponent}
          </div>
          <div>
            <span className={`font-medium ${styles.title}`}>{action}</span>
            {description && (
              <span className={`ml-2 text-sm ${styles.description}`}>— {description}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {why && (
            <button className={`text-xs underline ${styles.why}`} title={why}>
              Why?
            </button>
          )}
          <button
            onClick={onClick}
            disabled={disabled || loading}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${styles.button} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? 'Loading...' : (buttonText || action)}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-5 rounded-xl border ${styles.container}`}>
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={`p-3 rounded-xl ${styles.icon} flex-shrink-0`}>
          {IconComponent}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className={`font-semibold ${styles.title}`}>
                Recommended: {action}
              </h4>
              {description && (
                <p className={`mt-1 text-sm ${styles.description}`}>
                  {description}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {secondaryAction && (
                <button
                  onClick={onSecondaryClick}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border border-current/20 ${styles.description} hover:bg-muted transition-colors`}
                >
                  {secondaryAction}
                </button>
              )}
              <button
                onClick={onClick}
                disabled={disabled || loading}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${styles.button} disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    {buttonText || action}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Why link */}
          {why && (
            <p className={`mt-2 text-xs ${styles.why}`}>
              <span className="opacity-60">Why this step?</span> {why}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NextBestAction;
